import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Clock, CheckCircle, AlertCircle, Plane, Send, Mail, Package } from 'lucide-react';
import type { AdminOutletContext, StoredNotification } from '../types';
import { clearNotifications, loadNotifications, markAllNotificationsRead, saveNotifications } from '../lib/skyluxStore';

function iconForType(type: string): React.ReactNode {
  switch (type) {
    case 'quote_sent':
      return <Send size={18} color="var(--accent)" />;
    case 'payment':
      return <CheckCircle size={18} color="var(--success)" />;
    case 'reminder':
      return <Clock size={18} color="var(--gold)" />;
    case 'email':
      return <Mail size={18} color="var(--accent)" />;
    case 'inventory':
      return <Package size={18} color="var(--text-muted)" />;
    case 'new_request':
      return <Plane size={18} color="var(--primary)" />;
    default:
      return <AlertCircle size={18} color="var(--danger)" />;
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'Just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return d.toLocaleDateString();
}

const AdminNotifications = () => {
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const [tick, setTick] = useState(0);

  React.useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, []);

  const notifications = useMemo(() => {
    void tick;
    return loadNotifications();
  }, [tick]);

  const filtered = useMemo(() => {
    void tick;
    const q = globalSearch.trim().toLowerCase();
    if (!q) return notifications;
    return notifications.filter(
      (n) => n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q)
    );
  }, [notifications, globalSearch, tick]);

  const markOneRead = (id: string) => {
    const next = loadNotifications().map((n) => (n.id === id ? { ...n, read: true } : n));
    saveNotifications(next);
    setTick((t) => t + 1);
  };

  const markAll = () => {
    markAllNotificationsRead();
    setTick((t) => t + 1);
  };

  const clearAll = () => {
    if (!window.confirm('Clear all notifications?')) return;
    clearNotifications();
    setTick((t) => t + 1);
  };

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '2.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Notifications</h2>
        <p style={{ color: 'var(--text-muted)' }}>Live feed from quotes, payments, inventory, and email actions (stored in this browser).</p>
      </div>

      <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700' }}>Recent updates</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={markAll} style={{ fontSize: '0.875rem', color: 'var(--accent)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}>
              Mark all read
            </button>
            <button type="button" onClick={clearAll} style={{ fontSize: '0.875rem', color: 'var(--danger)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}>
              Clear all
            </button>
          </div>
        </div>

        <div style={{ display: 'grid' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              No notifications yet. Send a quote, update flights, or complete a payment to populate this list.
            </div>
          ) : (
            filtered.map((notif: StoredNotification) => (
              <button
                key={notif.id}
                type="button"
                onClick={() => !notif.read && markOneRead(notif.id)}
                style={{
                  padding: '1.5rem 2rem',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  gap: '1.5rem',
                  textAlign: 'left',
                  width: '100%',
                  background: notif.read ? 'white' : '#f8fafc',
                  borderLeft: notif.read ? 'none' : '3px solid var(--accent)',
                  cursor: 'pointer',
                  borderRight: 'none',
                  borderTop: 'none',
                }}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f1f5f9', flexShrink: 0 }}>
                  {iconForType(notif.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <p style={{ fontWeight: '700' }}>{notif.title}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>{formatWhen(notif.at)}</p>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{notif.message}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications;
